import {
  Typography,
  Box,
  Card,
  CardContent,
  styled,
  Skeleton
} from '@mui/material'
import DisplayValue from 'components/DisplayValue'
import { IPatientHistoricalValuesResponse } from 'types/patient/types'
import { decimalToString } from 'utils/decimal'
import dayjs from 'dayjs'
import { timeZone } from 'components/Graphs/LinesWithBar/utils/processTimeIntervals'
import getPercentile from 'utils/percentile'
import { useParams } from 'react-router-dom'

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2)
}))

const StyledCard = styled(Card)(({ theme }) => ({
  width: '30%',
  background: 'none',
  border: 'none',
  boxShadow: 'none'
}))

type ValueType = string | number | null

const hideIC90DataList: string[] = [
  'blood_pressure_systolic',
  'sleep_total',
  'steps'
]

interface IHealthDataSummaryProps {
  loading: boolean
  historicalValues: IPatientHistoricalValuesResponse[]
  valueType: string
  separator?: string
  showAllData?: boolean
  formatValue?: (value: ValueType) => ValueType
  summaryData?: string[]
  period?: string
  mean?: (
    values: { created_at: Date; value: number }[],
    period: string
  ) => number
  hideIC90?: boolean
}

const HealthDataSummary: React.FC<IHealthDataSummaryProps> = ({
  loading,
  historicalValues,
  valueType,
  separator,
  formatValue,
  showAllData = false,
  summaryData: summaryDataKeys = ['latest', 'min', 'max', 'average'],
  period = 'day',
  mean
}) => {
  // query params
  const { data } = useParams<{
    data: string
  }>()
  const now = dayjs().tz(timeZone)
  const startDateOfWeek = now
    .subtract(6, 'day')
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
  const summaries = historicalValues.map(histValues => {
    const filteredHist = histValues.values.filter(item => {
      if (period == 'week') {
        return !dayjs(item.created_at).isBefore(startDateOfWeek)
      }
      return true
    })

    const values = filteredHist.map(item => item.value)

    const latest = values.length
      ? filteredHist.slice(1).reduce((_latest, current) => {
          return new Date(current.created_at) > new Date(_latest.created_at)
            ? current
            : _latest
        }, filteredHist[0]).value
      : null

    const average = mean
      ? mean(filteredHist, period)
      : filteredHist.length > 0
        ? filteredHist.reduce((a, b) => Number(a) + Number(b.value), 0) /
          filteredHist.length
        : 0

    const { min, max } = getPercentile(values, 5)
    // TODO: remove comments if needed
    // const max = orderedValues?.length
    //   ? Math.max(...values.filter(v => !Number.isNaN(v)))
    //   : null
    // const min = values?.length
    //   ? Math.min(...values.filter(v => !Number.isNaN(v)))
    //   : null
    const total = values.reduce((sum, val) => sum + Number(val), 0)

    return {
      latest,
      average: formatValue ? average : decimalToString(average),
      max,
      min,
      total
    } as {
      [key: string]: ValueType
    }
  })

  const hideIC90 = hideIC90DataList.includes(data ?? '')

  const summaryData = [
    { title: 'Último', key: 'latest' },
    { title: hideIC90 ? 'Mínimo' : 'Mínimo (IC 90%)', key: 'min' },
    { title: hideIC90 ? 'Máximo' : 'Máximo (IC 90%)', key: 'max' },
    { title: 'Média', key: 'average' },
    { title: 'Total', key: 'total' }
  ].filter(data => summaryDataKeys.includes(data.key))

  return (
    <StyledBox mt={2}>
      {summaryData.map((data, i) => (
        <StyledCard key={i}>
          <CardContent>
            {loading ? (
              <Skeleton sx={{ margin: '0 auto' }} width={50} variant='text' />
            ) : (
              <DisplayValue
                values={[
                  {
                    value: showAllData
                      ? summaries
                          .filter(s => s[data.key])
                          .map(s => s[data.key])
                          .join(separator)
                      : formatValue
                        ? formatValue(summaries?.[0]?.[data.key])
                        : summaries?.[0]?.[data.key],
                    type: valueType
                  }
                ]}
                align='center'
              />
            )}
            <Typography align='center' variant='body2' fontWeight='bold'>
              {data.title}
            </Typography>
          </CardContent>
        </StyledCard>
      ))}
    </StyledBox>
  )
}

export default HealthDataSummary
